import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PaymentConfig {
  stripe: boolean;
  razorpay: boolean;
  preferred: 'stripe' | 'razorpay' | null;
}

interface RazorpaySubscription {
  subscriptionId: string;
  shortUrl: string;
  amount: number;
  currency: string;
  keyId: string;
  planName: string;
  email: string;
  userId: string;
  customerId: string;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

export function usePaymentProvider() {
  const queryClient = useQueryClient();

  // Check which payment providers are configured
  const { data: paymentConfig, isLoading: isCheckingConfig } = useQuery({
    queryKey: ['payment-config'],
    queryFn: async (): Promise<PaymentConfig> => {
      const { data, error } = await supabase.functions.invoke('check-payment-config');
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Stripe checkout mutation
  const stripeCheckout = useMutation({
    mutationFn: async ({ priceId, isYearly }: { priceId: string; isYearly: boolean }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { action: 'create-checkout-session', priceId, isYearly },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start checkout');
    },
  });

  // Razorpay subscription mutation (using proper Subscriptions API)
  const razorpayCheckout = useMutation({
    mutationFn: async ({ plan, isYearly }: { plan: string; isYearly: boolean }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('create-razorpay-subscription', {
        body: { plan, isYearly },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      return data as RazorpaySubscription;
    },
    onSuccess: (subscriptionData) => {
      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => openRazorpayCheckout(subscriptionData);
        document.body.appendChild(script);
      } else {
        openRazorpayCheckout(subscriptionData);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create subscription');
    },
  });

  const openRazorpayCheckout = (subscriptionData: RazorpaySubscription) => {
    const options = {
      key: subscriptionData.keyId,
      subscription_id: subscriptionData.subscriptionId, // Use subscription_id instead of order_id
      name: 'QR Code Generator',
      description: subscriptionData.planName,
      prefill: {
        email: subscriptionData.email,
      },
      theme: {
        color: '#7c3aed',
      },
      handler: function (response: any) {
        // Payment successful - subscription is now active
        toast.success('Subscription activated! Welcome to ' + subscriptionData.planName);
        // Invalidate queries to refresh subscription status
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
        queryClient.invalidateQueries({ queryKey: ['usage'] });
        // Reload to refresh subscription status
        setTimeout(() => window.location.reload(), 1500);
      },
      modal: {
        ondismiss: function () {
          toast.info('Subscription cancelled');
        },
        confirm_close: true,
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  // Customer portal (Stripe only)
  const openCustomerPortal = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { action: 'create-portal-session' },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to open billing portal');
    },
  });

  const subscribe = async (plan: string, isYearly: boolean, stripePriceId?: string) => {
    if (!paymentConfig?.preferred) {
      toast.error('No payment provider configured. Please contact support.');
      return;
    }

    if (paymentConfig.preferred === 'stripe' && stripePriceId) {
      stripeCheckout.mutate({ priceId: stripePriceId, isYearly });
    } else if (paymentConfig.preferred === 'razorpay') {
      razorpayCheckout.mutate({ plan, isYearly });
    } else {
      toast.error('Payment not available for this plan');
    }
  };

  return {
    paymentConfig,
    isCheckingConfig,
    subscribe,
    openCustomerPortal: openCustomerPortal.mutate,
    isSubscribing: stripeCheckout.isPending || razorpayCheckout.isPending,
    isOpeningPortal: openCustomerPortal.isPending,
  };
}
